// LayoutEditorInspectorPreview.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// CSS
import './LayoutEditorInspectorPreview.css';

// Icons
import Icon from 'svg/Icon';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * LayoutEditorInspectorPreview
 *
 * Renders the preview record picker shown at the top of the layout inspector.
 *
 * @returns {ReactNode} The rendered component.
 */
const LayoutEditorInspectorPreview = ({
  emptyMessage = '',
  loading = false,
  onSelectRecord,
  records = [],
  selectedRecordKey = '',
  typeDef = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const resolvedRecords = useMemo(() => (
    (Array.isArray(records) ? records : []).filter((record) => normalizeString(record?.key))
  ), [records]);

  // ----- RENDER ----- //

  return (
    <section className="layout-editor-inspector-section layout-editor-inspector-preview">
      <div
        className="layout-editor-inspector-section-label"
        style={{
          color: theme.foregroundMuted,
        }}
      >
        Preview Data
      </div>

      {resolvedRecords.length ? (
        <div className="layout-editor-inspector-preview-list">
          {resolvedRecords.map((record) => {
            const isSelected = selectedRecordKey === record.key;
            const { iconKey, iconPath } = resolveNodeIcon({
              node: record,
              typeDef,
            });

            return (
              <button
                className={[
                  'layout-editor-inspector-preview-record',
                  isSelected ? 'is-selected' : '',
                ].filter(Boolean).join(' ')}
                style={{
                  backgroundColor: isSelected ? theme.backgroundHover : theme.background,
                  borderColor: isSelected ? theme.foregroundMuted : theme.outlineMuted,
                  color: theme.foreground,
                }}
                key={record.key}
                aria-pressed={isSelected}
                type="button"
                onClick={() => onSelectRecord?.(record.key)}
              >

                {/* PREVIEW RECORD ICON */}
                <span className="layout-editor-inspector-preview-record-icon">
                  <Icon
                    color={theme.foregroundMuted}
                    height={18}
                    iconKey={iconKey || undefined}
                    path={iconPath || undefined}
                    width={18}
                  />
                </span>

                {/* PREVIEW RECORD TITLE */}
                <span className="layout-editor-inspector-preview-record-title">
                  {normalizeString(record.title) || 'Untitled'}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div
          className="layout-editor-inspector-preview-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          {loading ? 'Loading records...' : (emptyMessage || 'No records available.')}
        </div>
      )}
    </section>
  );
};

// Dev Tools
LayoutEditorInspectorPreview.displayName = 'LayoutEditorInspectorPreview';

export default LayoutEditorInspectorPreview;
